<template>
  <div class="hr-field-date__container">
    <p class="hr-field-date__label">{{ value.label }}</p>
    <co-date-picker
      class="hr-field-date"
      size="compact"
      v-model="state.date"
      placeholder="dd/mm/yyyy"
      format="dd/MM/yyyy"
      @blur="handleBlur"
      @selected="updateValue"
      @opened="handleFocused"
      :disabled-dates="disabledDates"
      :typeable="true"
      :inline="false"
    >
    </co-date-picker>
    <div v-if="state.invalid" class="hr-field-date__invalid-message">
      {{ value.validation.errorMessage }}
    </div>
    <div
      v-if="!state.invalid && errorMessage"
      class="hr-field-date__invalid-message"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { reactive } from 'vue';
import { CoDatePicker } from '@deputy/copilot2';
import { DateTime } from 'luxon';

const MIN_DATE = '1850-01-01';

export default {
  name: 'hr-field-date',
  components: { CoDatePicker },
  props: {
    value: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  emits: ['blur', 'focus', 'update:value'],
  setup(props, { emit }) {
    const TODAY = DateTime.now();
    const state = reactive({
      date: formatDate(props.value?.value),
      invalid: null,
    });

    const disabledDates = {
      to: DateTime.fromISO(MIN_DATE).startOf('day').toJSDate(),
      from: TODAY.toJSDate(),
    };

    function formatDate(dateString) {
      //replaces the - to / so it doesn't go one day off
      return dateString ? new Date(dateString?.replace(/-/g, '/')) : '';
    }

    function dateEmit(evt) {
      let val = null;
      if (!state.date || !(state.date instanceof Date)) {
        emit(evt, val);
        return;
      }

      val = DateTime.fromJSDate(state.date).toFormat('yyyy-MM-dd');
      emit(evt, val);
    }

    function handleBlur() {
      if (state.invalid) {
        state.date = '';
      }
      dateEmit('blur', state.date);
    }

    function isDateGreaterThanToday() {
      return (
        state.date > DateTime.fromJSDate(disabledDates.from).startOf('day')
      );
    }

    function isDateLesserThanMinDate() {
      return state.date < DateTime.fromJSDate(disabledDates.to);
    }

    function validateDate() {
      state.invalid = false;
      if (isDateGreaterThanToday() || isDateLesserThanMinDate()) {
        state.invalid = true;
      }
    }

    function handleFocused() {
      dateEmit('focus');
    }

    function updateValue(newDate) {
      state.date = DateTime.fromJSDate(newDate).startOf('day');
      validateDate();
    }

    return {
      state,
      handleBlur,
      handleFocused,
      updateValue,
      disabledDates,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/core/style-vars.scss';

.hr-field-date {
  width: fit-content;
}

.hr-field-date__invalid-message {
  color: var(--co-text-attention);
  margin-top: 5px;
}

.hr-field-date__label {
  margin-bottom: 4px;
  font-weight: 600;
  color: var(--co-text-primary);
}

@media only screen and (max-width: 768px) {
  .hr-field-date__container,
  .hr-field-date__invalid-message,
  :deep(.co-input__inner),
  :deep(.co-input__inner:focus) {
    font-size: 16px;
  }
}
</style>
